import {
  Avatar,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from '@mui/material'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import ReactCardFlip from 'react-card-flip'
import { DeepMap, FieldError } from 'react-hook-form'
import { IconButton, ItemImageArea } from 'utoniq-ui'

import { FileType, UploadBox } from '@/components/organisms'
import { config } from '@/config'
import { StorageImage, useTranslation } from '@/core'
import { useDeviceInfo } from '@/core/native/device'
import { PickFile } from '@/core/native/file'

export type ConsoleCampaignItem = {
  rarity: {
    id: 'common' | 'rare' | 'superrare'
    name: string
  }
  hasBenefit: boolean
  benefit: {
    uri?: string
    originalFile?: PickFile | null
    type: FileType
  }
  image: {
    front: {
      fileUrl: string
      tmpPath: string
    }
    back: { uri: string }
  }
}

type ConsoleCampaignItemBoxProps = {
  item: ConsoleCampaignItem
  errors?: DeepMap<ConsoleCampaignItem, FieldError>
  onUploadFile: (fileType: 'image' | 'benefit') => Promise<void>
  onRemoveFile: () => void
  uploading?: boolean
}

const rarityImage: Record<'common' | 'rare' | 'superrare', string> = {
  common: `${config.commonStorageUrl}images/rarity/RarityOne.svg`,
  rare: `${config.commonStorageUrl}images/rarity/RarityTwo.svg`,
  superrare: `${config.commonStorageUrl}images/rarity/RarityThree.svg`,
}

export const ConsoleCampaignItemBox: FC<ConsoleCampaignItemBoxProps> = ({
  item: { rarity, image, benefit, hasBenefit },
  errors,
  onUploadFile,
  onRemoveFile,
  uploading,
}) => {
  const { t } = useTranslation('consoleCampaignDetail')

  const errorMessage = useMemo(() => {
    if (!errors || (!errors.image && !errors.benefit)) {
      return ''
    }
    return t('pleaseAddThumbnail')
  }, [errors, t])

  return (
    <Card
      elevation={0}
      sx={{
        borderColor: errorMessage ? 'error.main' : 'grey.100',
        bgcolor: 'background.default',
        borderStyle: 'solid',
        borderRadius: 2,
        position: 'relative',
      }}
    >
      <Stack
        direction="row"
        spacing={3}
        sx={{ px: 5, py: 3, bgcolor: 'grey.50' }}
        alignItems="center"
      >
        <Avatar
          variant="square"
          src={rarityImage[rarity.id]}
          sx={{
            height: 20,
            width: 'fit-content',
          }}
        />
        <Typography variant="h5">{rarity.name}</Typography>
      </Stack>
      <CardContent sx={{ px: 5, pt: 4, pb: 6 }}>
        {errorMessage && (
          <Stack justifyContent="center">
            <Typography
              variant="caption"
              align="center"
              sx={{ color: 'error.main', py: 4 }}
            >
              {errorMessage}
            </Typography>
          </Stack>
        )}
        <ThumbnailBox
          onUpload={onUploadFile}
          image={image}
          uploading={!!uploading}
        />
        {hasBenefit && (
          <OptionBox
            benefit={benefit}
            onUpload={onUploadFile}
            onRemove={onRemoveFile}
          />
        )}
      </CardContent>
    </Card>
  )
}

type ThumbnailBoxProps = {
  image?: ConsoleCampaignItemBoxProps['item']['image']
  onUpload: ConsoleCampaignItemBoxProps['onUploadFile']
  uploading: boolean
}
const ThumbnailBox: FC<ThumbnailBoxProps> = ({
  image,
  onUpload,
  uploading,
}) => {
  const { t } = useTranslation('consoleCampaignDetail')
  const [isFlipped, setIsFlipped] = useState<boolean>(false)
  const [activeUploading, setActiveUploading] = useState<boolean>(false)
  const handleClickUpload = useCallback(async () => {
    setActiveUploading(true)
    await onUpload('image')
  }, [onUpload])

  useEffect(() => {
    if (!uploading && image?.front?.fileUrl && activeUploading) {
      setActiveUploading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploading])

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1">{t('addThumbnail')}</Typography>
      <Typography variant="caption" color="text.hint">
        {t('addThumbnailDesc')}
      </Typography>
      {activeUploading && uploading && (
        <Stack alignItems="center">
          <StorageImage
            target="common"
            src="images/console/loading.png"
            width={80}
          />
        </Stack>
      )}
      {image?.front?.fileUrl && (
        <Stack
          alignItems="center"
          spacing={3}
          display={activeUploading && uploading ? 'none' : 'flex'}
        >
          <ReactCardFlip isFlipped={isFlipped}>
            <ItemImageArea size="large" src={image.front.fileUrl} />
            <ItemImageArea size="large" src={image.back.uri} />
          </ReactCardFlip>
          <IconButton.Rotate
            bgcolor="primary.main"
            color="white"
            onClick={() => setIsFlipped(prevState => !prevState)}
          />
          <Button variant="text" color="primary" onClick={handleClickUpload}>
            {t('changeTheImage')}
          </Button>
        </Stack>
      )}
      {!(activeUploading && uploading) && !image?.front?.fileUrl && (
        <Button
          variant="outlined"
          size="medium"
          sx={{ width: 'fit-content' }}
          onClick={handleClickUpload}
        >
          {t('selectFile')}
        </Button>
      )}
    </Stack>
  )
}

type OptionBoxProps = {
  benefit?: ConsoleCampaignItemBoxProps['item']['benefit']
  onUpload: ConsoleCampaignItemBoxProps['onUploadFile']
  onRemove: () => void
}
const OptionBox: FC<OptionBoxProps> = ({ benefit, onUpload, onRemove }) => {
  const { t } = useTranslation('consoleCampaignDetail')

  const handleClickUpload = useCallback(() => {
    return onUpload('benefit')
  }, [onUpload])
  const handleClickRemove = useCallback(() => {
    onRemove()
  }, [onRemove])

  /**
   * @todo
   * Androidで動画リソースがアップロードできない不具合があるため、一時的に特典アップローダをAndroidのときのみ非表示にしている。
   * https://app.clickup.com/t/908127/UT-5411
   */
  const { deviceInfo } = useDeviceInfo()
  if (deviceInfo?.platform === 'android') return null

  return (
    <Stack spacing={2} sx={{ pt: 5 }}>
      <Typography variant="subtitle1">{t('addBenefit')}</Typography>
      <Stack spacing={1}>
        <Typography variant="caption">{t('addBenefitNote')}</Typography>
        <Typography variant="caption" color="text.hint">
          {t('addBenefitDesc')}
        </Typography>
      </Stack>
      {benefit?.originalFile ? (
        <UploadBox
          type={benefit?.type}
          onClick={handleClickUpload}
          applied
          onRemove={handleClickRemove}
          filename={benefit.originalFile.name}
        />
      ) : (
        <Button
          variant="outlined"
          size="medium"
          sx={{ width: 'fit-content' }}
          onClick={handleClickUpload}
        >
          {t('selectFile')}
        </Button>
      )}
    </Stack>
  )
}
